import { create } from 'zustand'

/**
 * Minimal toast/banner queue (Phase 3 error-handling). No new dependency — a
 * tiny zustand store rendered once by `<Toaster/>` at app root. Components push
 * a toast via the `useToast` hook (or directly through `useToastStore`); the
 * `transient` branch of `useApiError` is the primary producer.
 *
 * Kinds map to the existing `--color-status-*` tokens used across the app.
 */
export type ToastKind = 'error' | 'success' | 'info'

export interface Toast {
  id: string
  kind: ToastKind
  message: string
}

/** Auto-dismiss delay; a toast removes itself after this unless dismissed. */
export const TOAST_TTL_MS = 6000

interface ToastState {
  toasts: Toast[]
  /** Push a toast and return its id. Auto-dismisses after {@link TOAST_TTL_MS}. */
  push: (kind: ToastKind, message: string) => string
  /** Remove a toast by id (no-op if already gone). */
  dismiss: (id: string) => void
  /** Clear the whole queue (used on route changes / teardown). */
  clear: () => void
}

let counter = 0
function nextId(): string {
  counter += 1
  return `toast-${Date.now()}-${counter}`
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  push: (kind, message) => {
    const id = nextId()
    set((s) => ({ toasts: [...s.toasts, { id, kind, message }] }))
    // Auto-dismiss. `setTimeout` is fine in the browser; in jsdom tests it runs
    // under fake timers when the test opts in, otherwise the test dismisses
    // explicitly or unmounts before the timer fires.
    if (typeof window !== 'undefined') {
      window.setTimeout(() => get().dismiss(id), TOAST_TTL_MS)
    }
    return id
  },

  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  clear: () => set({ toasts: [] }),
}))
